import React from 'react';
import PropTypes from 'prop-types';
import { Surveys } from 'utils/types';
import { SFSurveysTenantLanguage } from "utils/types";
import css from './Survey.css'
import StickyFooterLayout from 'common/StickyFooterLayout';
import ProgressSurveyTableContainer from "./ProgressSurveyTableContainer";
import ProgressSurveyRow from './ProgressSurveyRow';
import Toast from 'common/Toast';
import ErrorMessage from 'common/ErrorMessage';
import Confirmation from './Confirmation';
import SurveyFooter from './SurveyFooter';
import SurveyHeader from './SurveyHeader';
import TableHeader from './TableHeader';
import QuestionDefinitionView from './QuestionDefinitionView';
import { clearLastViewedSurvey, getSurveyResponseJsonBody, loadAutosaveConfigs, searchForStudents } from 'utils/surveys';
import { apiErrorMessage, fetchAuthenticatedStarfishJSON } from 'utils/request';
import { cloneDeep, forOwn, isEmpty, isObject, isUndefined, union, without } from 'lodash';

export default class Survey extends React.Component {

  constructor() {
    super();
    this.state = {
      showQuestionDefinitions: window.innerWidth >= 2560, // Open drawer by default on XXL screens
      changesSinceLastDraft: false,
      failedSaveAttempts: 0,
      updatedDateBySurvey: {},
      isSavingDraft: false,
      showToast: false,
      showToastMessage: null,
      surveyAnswers: null,
      surveyQuestions: null,
      checkedQuestionsByStudent: {},
      answersByStudent: {},
      successfulAnswerSubmit: false,
      searchText: "",
      showSearchBox: false,
      isCurrentlySubmitting: false
    };
    this.errorRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    // If survey has changed, reset any filled information
    const switchedSurvey = this.props.surveyId !== prevProps.surveyId;
    if ( switchedSurvey ) {
      const body = getSurveyResponseJsonBody(
        prevProps.surveyId,
        this.state.checkedQuestionsByStudent,
        this.state.surveyAnswers,
        this.state.answersByStudent
      );
      this.saveSurveyDraft(body, prevProps.surveyId, true);
      this.setState({successfulAnswerSubmit: false, searchText: "", showSearchBox: false});
    }
  }

  componentDidMount() {
    const [autosaveIntervalMs, maxFailedAttempts] = loadAutosaveConfigs();
    if (autosaveIntervalMs && maxFailedAttempts) {
      this.interval = setInterval(() => this.autosaveTrigger(maxFailedAttempts), autosaveIntervalMs);
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  autosaveTrigger = (maxFailedAttempts) => {
    const { changesSinceLastDraft, isSavingDraft, failedSaveAttempts } = this.state;
    if (changesSinceLastDraft && !isSavingDraft && failedSaveAttempts < maxFailedAttempts) this.saveSurveyDraft(null, this.props.surveyId, true);
  };

  get currentBody() {
    return getSurveyResponseJsonBody(
      this.props.surveyId,
      this.state.checkedQuestionsByStudent,
      this.state.surveyAnswers,
      this.state.answersByStudent
    );
  }

  saveSurveyResponse = () => {
    if (this.state.isCurrentlySubmitting) {
      return;
    }
    this.setState({isCurrentlySubmitting: true});
    const URL = `../survey/saveSurveyResponse.html`;
    fetchAuthenticatedStarfishJSON(URL, '', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: this.currentBody
    })
      .then(() => this.setState({
        successfulAnswerSubmit: true,
        isCurrentlySubmitting: false
      }))
      .catch(e => {
        console.error(`Survey submit failed at ${new Date()}: ${apiErrorMessage(e)}`);
        this.props.onFailure(`Unable to submit ${this.props.progressSurveyWords.singular.survey} at ${new Date()}, please clear this message, try again, and contact support if it continues.`);
        this.setState({isCurrentlySubmitting: false});
      });
  };

  // Manual saves will show a toast message on success
  saveSurveyDraft = (body, surveyId, isAutosave = false) => {
    this.setState({ isSavingDraft: true });
    const URL = `../survey/saveSurveyResponseDraft.html`;
    fetchAuthenticatedStarfishJSON(URL, '', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: body || this.currentBody
    })
      .then(({ formattedUpdatedDate }) => {
        this.setState({
          changesSinceLastDraft: false,
          failedSaveAttempts: 0,
          updatedDateBySurvey: Object.assign(
            {},
            this.state.updatedDateBySurvey,
            { [surveyId || this.props.surveyId]: formattedUpdatedDate })
        });
        if (!isAutosave) this.setState({ showToast: true, showToastMessage: "Your changes were saved." })
      })
      .catch(e => {
        console.error(`Draft save failed at ${new Date()}: ${apiErrorMessage(e)}`);
        this.props.onFailure(`Unable to save ${this.props.progressSurveyWords.singular.survey} at ${new Date()}, please clear this message and contact support if it continues.`);
        this.setState({ failedSaveAttempts: this.state.failedSaveAttempts + 1 });
      })
      .then(() => this.setState({ isSavingDraft: false }));
  };

  updateAnswersByStudent(allowDefaultOverride = true) {
    // Both questions and answers must be loaded before updating
    if (!this.state.surveyQuestions || !this.state.surveyAnswers) return null;

    // First create an array with default answers for every question, if any
    const defaultAnswers = this.state.surveyQuestions
      .filter(({ type, includeDefaultComments, defaultRaiseComments }) =>
        type !== "default" && includeDefaultComments && defaultRaiseComments)
      .map(({ defaultRaiseComments, id }) => ({ id, comment: defaultRaiseComments, isValid: true }));

    // Then iterate over all answers and fill the object with the default value
    // evaluated above. Finally, if there are saved answers, we override the default
    // value with them
    const answersByStudent = {};

    // Also make sure to check questions which already have an answer
    // (default comment doesn't count)
    const checkedQuestionsByStudent = {};

    this.state.surveyAnswers.forEach(({ answers, id }) => {
      const savedAnswers = cloneDeep(defaultAnswers);
      const checkedQuestions = [];
      answers.forEach(answer => {
        if (isObject(answer) && typeof answer.comment !== "undefined") {
          checkedQuestions.push(answer.id);
          const index = savedAnswers.findIndex(savedAnswer => savedAnswer.id === answer.id);
          const defaultComment = index > -1 ? savedAnswers[index].comment : "";
          const newAnswer = {
            id: answer.id,
            comment: !isUndefined(answer.comment) && allowDefaultOverride ? answer.comment : defaultComment,
            shouldValidate: false,
            isValid: !isEmpty(answer.comment)
          };
          if (index === -1) savedAnswers.push(newAnswer);
          else savedAnswers[index] = newAnswer;
        }
      });
      // We only save it if has either a saved answer or a default answer
      if (savedAnswers.length > 0) answersByStudent[id] = savedAnswers;
      if (checkedQuestions.length > 0) checkedQuestionsByStudent[id] = checkedQuestions;
    });
    this.setState({ answersByStudent, checkedQuestionsByStudent });
  }

  onQuestionChecked = (studentId, questionId) => {
    const studentQuestions = this.state.checkedQuestionsByStudent[studentId];
    const checkedQuestions = !studentQuestions || studentQuestions.indexOf(questionId) === -1
      ? union(studentQuestions, [questionId])
      : without(studentQuestions, questionId);
    this.setState({
      checkedQuestionsByStudent: Object.assign(
        {},
        this.state.checkedQuestionsByStudent,
        { [studentId]: checkedQuestions }
      ),
      changesSinceLastDraft: true,
      failedSaveAttempts: 0
    });
  }

  onAnswerChanged = (studentId, questionId, comment, isBlur = false) => {
    const questionIsRequired = this.requiredQuestions.includes(questionId);

    // This is the new answer for the given question
    const isValid = !questionIsRequired || (questionIsRequired && !isEmpty(comment));
    const newAnswer = {
      id: questionId,
      comment,
      shouldValidate: true,
      isValid
    };

    // Get the current answer (if any) for the given question
    const studentAnswers = this.state.answersByStudent[studentId];
    const currentAnswer = studentAnswers && studentAnswers.find(({ id }) => id === questionId);

    // If there is a current answer, simply update the answers object by filtering the
    // current one and concatenating with the new one. Else, create a new answer array if no
    // answer was yet provided, or simply add the new one to the answers array
    const newStudentAnswers = currentAnswer
      ? studentAnswers.filter(({ id }) => id !== questionId).concat([newAnswer])
      : (studentAnswers ? studentAnswers.concat([newAnswer]) : [newAnswer]);

    const newState = {
      answersByStudent: Object.assign(
        {},
        this.state.answersByStudent,
        { [studentId]: newStudentAnswers }
      ),
      changesSinceLastDraft: true,
      failedSaveAttempts: 0
    };

    // Finally, update the state for the given student
    // Note that we need to wrap the setState in a setTimeout in order to prevent
    // a bug where the onClick event won't be called. This happens because we are
    // adding an element to the DOM on blur, which is "pushing" all other elements
    // down and cancelling the onClick event.
    // See: https://github.com/facebook/react/issues/2291 and https://github.com/facebook/react/issues/4210
    if ( isBlur ) setTimeout(() => { this.setState(newState); }, 150);
    else this.setState(newState);

    if (!isValid && isBlur) this.setState({ showToast: true, showToastMessage: "There are incomplete fields." });
  }

  renderStudent = (isMobile, student, index) => {
    return (
      <ProgressSurveyRow
        key={index}
        student={student}
        getStudentFolderLink={this.props.getStudentFolderLink}
        surveyQuestions={this.state.surveyQuestions}
        checkedQuestions={this.state.checkedQuestionsByStudent[student.id]}
        onQuestionChecked={this.onQuestionChecked}
        answers={this.state.answersByStudent[student.id]}
        onAnswerChanged={this.onAnswerChanged}
        isMobile={isMobile}
      />
    );
  }

  get isSurveyUnchanged() {
    // If no checkboxes marked it means no changes have been made
    if (isEmpty(this.state.checkedQuestionsByStudent)) return true;
    let hasCheckedQuestion = false;
    forOwn(this.state.checkedQuestionsByStudent, questions => {
      if (questions.length > 0) hasCheckedQuestion = true;
    });
    return !hasCheckedQuestion;
  }

  get requiredQuestions() {
    return isEmpty(this.state.surveyQuestions)
      ? []
      : this.state.surveyQuestions
        .filter(({ commentRequired }) => commentRequired)
        .map(({ id }) => id);
  }

   // If strictMode is true, we solely check if, for all checked questions, their answers
  // are valid, irrespective of shouldValidate condition
  isSurveyValid(strictMode = false) {
    // If no question selected, then survey is valid
    if (isEmpty(this.state.checkedQuestionsByStudent)) return true;

    // No question is required, survey is valid always
    if (this.requiredQuestions.length === 0) return true;

    let isValid = true;

    // Iterate over all answers by student
    forOwn(this.state.answersByStudent, (answers, studentId) => {
      // If no question selected for current student, it can't be invalid
      const studentQuestions = this.state.checkedQuestionsByStudent[studentId];
      if (isEmpty(studentQuestions)) return;

      const invalidAnswers = answers
        .filter(({ id }) => studentQuestions.includes(id))
        .filter(({ id }) => this.requiredQuestions.includes(id))
        .filter(answer => !answer.isValid && (strictMode || answer.shouldValidate));

      // If invalidAnswers are found for any one question, stop checking questions and set whole survey invalid
      if (invalidAnswers.length > 0) {
        isValid = false;
        return false; // Early exit forOwn
      }
    });

    // If invalid, early return. Else, only proceed if in strict mode
    if (!isValid || !strictMode) return isValid;

    // Iterate over all checked questions by student
    forOwn(this.state.checkedQuestionsByStudent, (studentQuestions, studentId) => {
      const studentAnswers = this.state.answersByStudent[studentId];
      const invalidQuestions = studentQuestions
        .filter(questionId => this.requiredQuestions.includes(questionId))
        .filter(questionId => isEmpty(studentAnswers) || isEmpty(studentAnswers.find(({ id }) => id === questionId)));

      // If there is any invalid question (checked but without an answer), can't be valid in strict mode
      if (invalidQuestions.length > 0) {
        isValid = false;
        return false; // Early exit forOwn
      }
    });

    return isValid;
  }

  // This function checks for all invalid answers which weren't validated
  // using strict mode and mark them as invalid appropriately
  forceValidation = () => {
    const answersByStudent = {};

    forOwn(this.state.answersByStudent, (answers, studentId) => {
      const studentQuestions = this.state.checkedQuestionsByStudent[studentId];
      const newAnswers = answers
        .map(answer => !isEmpty(studentQuestions)
          && studentQuestions.includes(answer.id)
          && this.requiredQuestions.includes(answer.id)
          ? Object.assign({}, answer, { shouldValidate: true }) // Enforce validation
          : answer
        );
      answersByStudent[studentId] = newAnswers;
    });

    // Create an empty answer for every checked question which does not have an answer yet
    forOwn(this.state.checkedQuestionsByStudent, (studentQuestions, studentId) => {
      const answers = answersByStudent[studentId];
      studentQuestions
        .filter(questionId => this.requiredQuestions.includes(questionId))
        .filter(questionId => isEmpty(answers) || isEmpty(answers.find(({ id }) => id === questionId)))
        .forEach(questionId => {
          const newAnswer = { id: questionId, comment: "", shouldValidate: true, isValid: false };
          if (isEmpty(answers)) answersByStudent[studentId] = [newAnswer];
          else answersByStudent[studentId].push(newAnswer);
        });
    });

    this.setState({ answersByStudent });

    // Scrolls to top to display error message
    this.errorRef.current.scrollIntoView();
  }

  get toast() {
    if (!this.state.showToast) return null
    return (
      <Toast
        message={this.state.showToastMessage}
        onTimeout={() => this.setState({ showToast: false })}
      />
    )
  }

  get errorMessage() {
    return this.isSurveyValid()
      ? null
      : <ErrorMessage message="Required information is missing from one or more fields."/>;
  }

  onResetSurvey = () => {
    this.updateAnswersByStudent(false);
    this.setState({
      showToast: true,
      showToastMessage: "Your changes were discarded.",
      changesSinceLastDraft: true,
      checkedQuestionsByStudent: {} // Clear checked questions
    });
  }

  onCloseConfirmationScreen = () => {
    this.setState({ successfulAnswerSubmit: false });
    this.props.getSurveys();
    clearLastViewedSurvey();
  };

  render() {
    return (
      <StickyFooterLayout
        content={
          <div className={css['container']}>
            <div className={css['main-panel']}>
              <div ref={this.errorRef}>{this.errorMessage}</div>
              <div 
                className={css['content']}
                style={this.state.showQuestionDefinitions ? { marginRight: '10px', marginLeft: '10px' } : {}}>
                <SurveyHeader
                  surveys={this.props.surveys}
                  surveyId={this.props.surveyId}
                  updatedDateBySurvey={this.state.updatedDateBySurvey}
                  progressSurveyWords={this.props.progressSurveyWords}
                />
                <TableHeader
                  displaySearchBox={this.state.showSearchBox}
                  showSearchBox={() => this.setState({showSearchBox: true})}
                  hideSearchBox={() => this.setState({showSearchBox: false})}
                  onSearchChange={value => this.setState({searchText: value})}
                  displayInfoButton={this.state.surveyQuestions ? true : false}
                  onClickInformationButton={() => this.setState({showQuestionDefinitions: true})}
                />
                <ProgressSurveyTableContainer
                  surveyId={this.props.surveyId}
                  rebindStudentCards={this.props.rebindStudentCards}
                  onFailure={this.props.onFailure}
                  onFetch={(attr, data) => this.setState({[attr]: data})}
                  surveyQuestions={this.state.surveyQuestions}
                  surveyAnswers={searchForStudents(this.state.searchText, this.state.surveyAnswers)}
                  renderStudent={this.renderStudent}
                  onSurveyFullyLoaded={this.updateAnswersByStudent.bind(this)}
                />
              </div>
            </div>
            <Confirmation 
              isOpen={this.state.successfulAnswerSubmit}
              onClose={this.onCloseConfirmationScreen}
              surveys={this.props.surveys}
              surveyId={this.props.surveyId}
              progressSurveyWords={this.props.progressSurveyWords}
            />
            <QuestionDefinitionView
              showQuestionDefinitions={this.state.showQuestionDefinitions}
              surveyQuestions={this.state.surveyQuestions}
              onClose={() => this.setState({ showQuestionDefinitions: false })}
            />
            {this.toast}
          </div>
        }
        footer={
          <SurveyFooter 
            canClickOnSubmit={this.isSurveyValid() && !this.state.isCurrentlySubmitting}
            canSubmit={this.isSurveyValid(true)}
            forceValidation={this.forceValidation}
            onSubmitSurvey={this.saveSurveyResponse}
            onResetSurvey={this.onResetSurvey}
            onSaveDraft={() => this.saveSurveyDraft()}
            canSaveDraft={!this.state.isSavingDraft && this.state.changesSinceLastDraft}
            isSurveyUnchanged={this.isSurveyUnchanged}
            progressSurveyWords={this.props.progressSurveyWords}
            isCurrentlySubmitting={this.state.isCurrentlySubmitting}
          />
        }
      />
    );
  }
}

Survey.propTypes = {
  surveys: Surveys.isRequired,
  surveyId: PropTypes.number.isRequired,
  getStudentFolderLink: PropTypes.func.isRequired,
  rebindStudentCards: PropTypes.func.isRequired,
  onFailure: PropTypes.func.isRequired,
  getSurveys: PropTypes.func.isRequired,
  progressSurveyWords: SFSurveysTenantLanguage
};
